import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';
import '../proctor.css';

const TIER_META = {
  browser: { label: 'Browser', icon: 'Globe' },
  behavioral: { label: 'Behavioral', icon: 'Brain' },
  vision: { label: 'Vision', icon: 'Eye' },
  audio: { label: 'Audio', icon: 'Mic' },
};

const severityClass = {
  low: 'proctor-alert-low',
  medium: 'proctor-alert-medium',
  high: 'proctor-alert-high',
  critical: 'proctor-alert-critical',
};

function formatRelativeTime(timestamp) {
  if (!timestamp) return 'just now';
  const delta = Date.now() - new Date(timestamp).getTime();
  if (delta < 60000) return 'just now';
  if (delta < 3600000) return `${Math.floor(delta / 60000)}m ago`;
  return `${Math.floor(delta / 3600000)}h ago`;
}

const TrustGauge = ({ score }) => {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? 'var(--proctor-green)' : score >= 40 ? 'var(--proctor-yellow)' : 'var(--proctor-red)';

  return (
    <div className="proctor-gauge">
      <svg width="110" height="110" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={radius} className="proctor-gauge-track" />
        <circle
          cx="55"
          cy="55"
          r={radius}
          className="proctor-gauge-value"
          style={{ stroke: color, strokeDasharray: circumference, strokeDashoffset: offset }}
        />
      </svg>
      <div className="proctor-gauge-label">
        <span className="proctor-gauge-score">{score}</span>
        <span className="proctor-gauge-caption">Trust</span>
      </div>
    </div>
  );
};

const ProctorPanel = ({
  events,
  trustScore,
  visionReady,
  audioReady,
  visionUnavailable,
  isProctoring,
  onClose,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const lastAlertRef = useRef(null);

  const summary = useMemo(() => {
    const highSeverity = events.filter((event) => event.severity === 'high' || event.severity === 'critical').length;
    const tabAway = events.filter((event) => event.signal === 'tab_switch').length;
    const faceAlerts = events.filter((event) => event.signal === 'multiple_faces').length;
    return { highSeverity, tabAway, faceAlerts };
  }, [events]);

  useEffect(() => {
    const latest = events[0];
    if (!latest || latest.id === lastAlertRef.current) return;
    lastAlertRef.current = latest.id;
    if (latest.severity === 'high' || latest.severity === 'critical') {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = 880;
        gain.gain.value = 0.03;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.12);
      } catch {
        // Optional alert sound.
      }
    }
  }, [events]);

  return (
    <aside className="proctor-panel">
      <div className="proctor-panel-header">
        <div>
          <p className="proctor-panel-title">Proctor logs</p>
          <p className="proctor-panel-subtitle">Integrity alert timeline</p>
        </div>
        <button type="button" className="proctor-panel-close" onClick={onClose} title="Close proctor logs">
          <Icon name="X" size={15} />
        </button>
      </div>

      <div className="proctor-panel-body">
        <div className="proctor-summary-grid">
          <TrustGauge score={trustScore} />
          <div className="proctor-summary-stats">
            <div><span>{events.length}</span><small>Alerts</small></div>
            <div><span>{summary.highSeverity}</span><small>High</small></div>
            <div><span>{summary.tabAway}</span><small>Tab away</small></div>
            <div><span>{summary.faceAlerts}</span><small>Face alerts</small></div>
          </div>
        </div>

        <div className="proctor-tier-status">
          <span className={cn('proctor-tier-pill', isProctoring && 'is-active')}>
            <Icon name="Globe" size={12} /> Browser
          </span>
          <span className={cn('proctor-tier-pill', visionReady && 'is-active', visionUnavailable && 'is-warning')}>
            <Icon name="Eye" size={12} /> {visionUnavailable ? 'Vision off' : 'Vision'}
          </span>
          <span className={cn('proctor-tier-pill', audioReady && 'is-active')}>
            <Icon name="Mic" size={12} /> Audio
          </span>
        </div>

        <div className="proctor-alert-feed">
          {events.length === 0 ? (
            <div className="proctor-empty">
              <Icon name="ShieldCheck" size={24} />
              <p>No integrity alerts yet.</p>
            </div>
          ) : (
            events.map((event) => {
              const tier = TIER_META[event.tier] || TIER_META.browser;
              const expanded = expandedId === event.id;
              return (
                <button
                  key={event.id}
                  type="button"
                  className={cn('proctor-alert-card', severityClass[event.severity], expanded && 'is-expanded')}
                  onClick={() => setExpandedId(expanded ? null : event.id)}
                >
                  <div className="proctor-alert-top">
                    <span className="proctor-tier-badge">
                      <Icon name={tier.icon} size={12} />
                      {tier.label}
                    </span>
                    <span className="proctor-alert-time">{formatRelativeTime(event.timestamp)}</span>
                  </div>
                  <p className="proctor-alert-message">{event.message}</p>
                  <div className="proctor-alert-meta">
                    <span className="proctor-confidence">{Math.round((event.confidence || 0) * 100)}% confidence</span>
                    <span className="proctor-severity">{event.severity}</span>
                  </div>
                  <div className="proctor-confidence-bar">
                    <span style={{ width: `${Math.round((event.confidence || 0) * 100)}%` }} />
                  </div>
                  {expanded && event.details && (
                    <pre className="proctor-alert-details">{JSON.stringify(event.details, null, 2)}</pre>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </aside>
  );
};

export default ProctorPanel;
